/**
 * QC Service Layer
 * API wrapper for QC system endpoints
 */

import axios from 'axios';

const API_BASE = '/api/qc';

const qcService = {
  /**
   * Upload file for QC analysis
   * @param {File} file - Document file to analyze
   * @param {string} journal - Journal code (e.g., 'IJA', 'AIAN')
   * @param {string} articleId - Optional article ID
   * @returns {Promise} - Analysis ID and initial results
   */
  uploadFile: (file, journal, articleId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('journalCode', journal);
    if (articleId) {
      formData.append('articleId', articleId);
    }
    return axios.post(`${API_BASE}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Get all QC analyses with optional filters
   * @param {object} params - Query parameters (journal, status, limit, offset)
   * @returns {Promise} - List of analyses
   */
  getAnalyses: (params = {}) => {
    return axios.get(`${API_BASE}/analyses`, { params });
  },

  /**
   * Get specific QC analysis with all details
   * @param {number} id - Analysis ID
   * @returns {Promise} - Full analysis object
   */
  getAnalysis: (id) => {
    return axios.get(`${API_BASE}/analysis/${id}`);
  },

  /**
   * Get issues for specific analysis
   * @param {number} id - Analysis ID
   * @param {object} filters - Filters (category, severity)
   * @returns {Promise} - List of issues
   */
  getAnalysisIssues: (id, filters = {}) => {
    return axios.get(`${API_BASE}/analysis/${id}/issues`, { params: filters });
  },

  /**
   * Re-run analysis on existing file
   * @param {number} id - Analysis ID
   * @returns {Promise} - Updated analysis results
   */
  reanalyze: (id) => {
    return axios.put(`${API_BASE}/analysis/${id}/reanalyze`);
  },

  /**
   * Export analysis as PDF or CSV
   * @param {number} id - Analysis ID
   * @param {string} format - Export format ('pdf' or 'csv')
   * @returns {Promise} - File blob
   */
  exportAnalysis: (id, format = 'pdf') => {
    return axios.get(`${API_BASE}/analysis/${id}/export`, {
      params: { format },
      responseType: 'blob',
    });
  },

  /**
   * Get list of available journals
   * @returns {Promise} - List of journal codes and names
   */
  getJournals: () => {
    return axios.get(`${API_BASE}/journals`);
  },

  /**
   * Delete an analysis
   * @param {number} id - Analysis ID
   * @returns {Promise} - Deletion confirmation
   */
  deleteAnalysis: (id) => {
    return axios.delete(`${API_BASE}/analysis/${id}`);
  },
};

export default qcService;
